/**
 * Break Management Layout — persistent shell for /hr/breaks/* (BM-007)
 * ----------------------------------------------------------------------
 * Mounted ONCE by the parent route in App.js; tabs swap only the <Outlet />.
 * Top-to-bottom anatomy (identical on all four tabs):
 *
 *   1. Standard TitleFlow header (shared AppHeader — no nav pill highlighted)
 *   2. Breadcrumb — Dashboard › HR › Break Management › <subpage>
 *   3. H1 + tagline
 *   4. Sub-tab navigation — TitleFlow violet underline for active
 *   5. KPI strip — 4 cards (not on Inactivity Report, which brings its own KPIs)
 *   6. <page content>
 */

import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Coffee, Activity, Flag, SearchCheck, UserRoundCheck } from 'lucide-react';
import BreakBreadcrumb from './BreakBreadcrumb';
import BreakKpiStrip from './BreakKpiStrip';
import AppHeader from '../../shared/AppHeader';
import { BreakBanner } from './ui/BreakUi';

const TABS = [
  { to: '/hr/breaks/history', label: 'Break History',  icon: Coffee,   testid: 'break-tab-history' },
  { to: '/hr/breaks/active',  label: 'Active Breaks',  icon: Activity, testid: 'break-tab-active' },
  { to: '/hr/breaks/flagged', label: 'Flagged Breaks', icon: Flag,     testid: 'break-tab-flagged' },
  { to: '/hr/breaks/inactivity', label: 'Inactivity Report', icon: SearchCheck, testid: 'break-tab-inactivity', report: true },
];

const matchTab = (pathname) => TABS.find((t) => pathname === t.to || pathname.startsWith(t.to + '/'));

export const BreakManagementLayout = ({ children }) => {
  const { pathname } = useLocation();
  const tab = matchTab(pathname);
  const report = Boolean(tab?.report);

  return (
    <div data-testid="break-management-layout" className="min-h-screen bg-slate-50">
      <AppHeader activeView={null} />

      <div className="p-6 mx-auto max-w-[1600px]">
        <div data-testid="break-management-shell">
          <BreakBreadcrumb subpage={tab ? tab.label : 'Break Management'} />

          <div data-testid="break-management-title">
            <BreakBanner icon={UserRoundCheck} title="Break Management" quote="“Visibility today, a more productive tomorrow.”"
              subtitle="Review break activity, monitor live sessions, and act on flagged breaks across the team." testId="break-management-banner" />
          </div>
        </div>

        <nav className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" aria-label="Break Management tabs" data-testid="break-management-tabs">
          {TABS.map(({ to, label, icon: Icon, testid }) => {
            const active = tab?.to === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                aria-current={active ? 'page' : undefined}
                className={`bm-tab flex items-center gap-2 px-3 py-2.5 text-[13px] font-medium whitespace-nowrap rounded-t-md ${
                  active ? 'is-active text-violet-700' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Icon className={`w-4 h-4 ${active ? 'text-violet-600' : 'text-slate-400'}`} />
                {label}
              </Link>
            );
          })}
        </nav>

        {!report && <BreakKpiStrip />}

        <div>{children ?? <Outlet />}</div>
      </div>
    </div>
  );
};

export default BreakManagementLayout;
